import { DateTime } from 'luxon';

import {
  PortalAirportNode,
  PortalFlightItineraryGroupNode,
  PortalLeadFirstTripNode,
  PortalTripType,
} from '@app/services/api/api.types';

export type OptionDateChange = {
  routeLabel: string;
  copy: string;
};

export type TripRequestDatesInput = {
  firstTrip?: PortalLeadFirstTripNode;
  roundTripDepartureAt?: string | null;
  tripType?: PortalTripType;
};

type RequestLeg = {
  departureAt: string;
  departureAirport?: PortalAirportNode;
  arrivalAirport?: PortalAirportNode;
};

export const getOptionDateChanges = (
  request: TripRequestDatesInput,
  itineraries: PortalFlightItineraryGroupNode[] | undefined,
): OptionDateChange[] => {
  if (!itineraries?.length || !request.firstTrip?.departureAt) {
    return [];
  }

  const requestLegs = buildRequestLegs(request);
  if (!requestLegs.length) {
    return [];
  }

  const optionLegs = pickOptionLegs(itineraries, requestLegs.length);
  const changes: OptionDateChange[] = [];

  requestLegs.forEach((requestLeg, index) => {
    const optionLeg = optionLegs[index];
    if (!optionLeg) {
      return;
    }

    const optionDepartureAt = getOptionLocalDepartureAt(optionLeg);
    if (!optionDepartureAt) {
      return;
    }

    const dayDiff = getCalendarDayDiff(requestLeg.departureAt, optionDepartureAt);
    if (!dayDiff) {
      return;
    }

    changes.push({
      routeLabel: formatRouteLabel(optionLeg, requestLeg),
      copy: formatDayChangeCopy(dayDiff),
    });
  });

  return changes;
};

const getOptionLocalDepartureAt = (itinerary: PortalFlightItineraryGroupNode): string => {
  return (
    itinerary.segments?.[0]?.flightSegment?.departureAtAirportTimeZone ||
    itinerary.departureAt ||
    ''
  );
};

const buildRequestLegs = (request: TripRequestDatesInput): RequestLeg[] => {
  const firstTrip = request.firstTrip;
  if (!firstTrip?.departureAt) {
    return [];
  }

  const outbound: RequestLeg = {
    departureAt: firstTrip.departureAt,
    departureAirport: firstTrip.departureAirport,
    arrivalAirport: firstTrip.arrivalAirport,
  };

  const isRoundOrMulti =
    request.tripType === PortalTripType.Round || request.tripType === PortalTripType.Multi;

  if (!isRoundOrMulti || !request.roundTripDepartureAt) {
    return [outbound];
  }

  const returnLeg: RequestLeg = {
    departureAt: request.roundTripDepartureAt,
    ...(request.tripType === PortalTripType.Round
      ? {
          departureAirport: firstTrip.arrivalAirport,
          arrivalAirport: firstTrip.departureAirport,
        }
      : {}),
  };

  return [outbound, returnLeg];
};

const pickOptionLegs = (
  itineraries: PortalFlightItineraryGroupNode[],
  requestLegCount: number,
): PortalFlightItineraryGroupNode[] => {
  if (requestLegCount === 1) {
    return [itineraries[0]];
  }

  if (itineraries.length === 1) {
    return [itineraries[0]];
  }

  return [itineraries[0], itineraries[itineraries.length - 1]];
};

const toCalendarDate = (value: string): DateTime | null => {
  if (!value) {
    return null;
  }

  let date = DateTime.fromISO(value);
  if (!date.isValid) {
    const datePart = value.split(/[T ]/)[0];
    date = DateTime.fromISO(datePart);
  }

  return date.isValid ? date.startOf('day') : null;
};

const getCalendarDayDiff = (requestDate: string, optionDate: string): number => {
  const request = toCalendarDate(requestDate);
  const option = toCalendarDate(optionDate);

  if (!request || !option) {
    return 0;
  }

  return Math.round(option.diff(request, 'days').days);
};

const formatDayChangeCopy = (dayDiff: number): string => {
  const absDays = Math.abs(dayDiff);
  const dayLabel = absDays === 1 ? 'day' : 'days';
  const direction = dayDiff < 0 ? 'earlier' : 'later';

  return `Departs ${absDays} ${dayLabel} ${direction}`;
};

const formatAirportLabel = (
  airport: PortalAirportNode,
  requestAirport?: PortalAirportNode,
): string => {
  const showCode = !!requestAirport && airport.locationCode !== requestAirport.locationCode;

  return showCode ? `${airport.cityName} (${airport.locationCode})` : airport.cityName;
};

const formatRouteLabel = (
  optionLeg: PortalFlightItineraryGroupNode,
  requestLeg: RequestLeg,
): string => {
  const departure = formatAirportLabel(optionLeg.departureAirport, requestLeg.departureAirport);
  const arrival = formatAirportLabel(
    optionLeg.finalDestinationAirport || optionLeg.arrivalAirport,
    requestLeg.arrivalAirport,
  );

  return `${departure} → ${arrival}`;
};
